// Music time (§5, §13.4, §25). The score is locked to the round's tempo clock: a TempoMap turns
// beats into round ticks (normal TPB, then the Sudden Death TPB from its bar-snapped start), and a
// TickClock turns round ticks into AudioContext time.
//
// ViewClock follows the frame source: every frame reports (roundTick, the context time that is
// heard at that frame's timestamp). Its offset `ctxTime − tick/60` is smoothed; the sequencer reads
// the applied offset and re-anchors at the next bar line when the smoothed one has drifted more
// than 20 ms, or immediately after a jump (round start, seek, hard resync). One-shot cues that must
// land on a tick (hazards, the countdown) use the smoothed offset directly. Pure: no Web Audio.
import { TICK_HZ } from '@shared/constants';

export const DRIFT_REANCHOR_S = 0.02;
/** A frame whose offset differs this much is a discontinuity: re-anchor at once. */
export const JUMP_S = 0.25;
/** Frames kept for the fit, the window it spans and the fewest it uses. */
const FIT_MAX = 96;
const FIT_WINDOW_S = 1.2;
/** Weight time constant of the fit. */
const FIT_TAU_S = 0.6;
/** Curvature bound of the fit, s/tick² (a rate change of 2×/s at 60 ticks/s). */
const MAX_CURVE = 1 / (TICK_HZ * TICK_HZ * TICK_HZ);
const FIT_MIN = 8;
/** Residuals beyond max(3·MAD, this) are outliers. */
const OUTLIER_FLOOR_S = 0.004;
/** Consecutive discontinuous frames that make a jump. */
const JUMP_FRAMES = 3;
/** A frame's tick may trail a cue by the commit horizon plus this (one frame of rate noise). */
const DUE_SLACK_TICKS = 2;
/** The fit and the newest frame disagree on a cue's time by more than this: trust the frame. */
const CUE_STALE_S = 0.03;
/**
 * The shown tick advancing at under a quarter of real time over the last 150 ms is a freeze, not a
 * rate (9 ticks normally; ±1 tick of frame jitter cannot fake it).
 */
const FROZEN_WINDOW_S = 0.15;
const FROZEN_RATE = TICK_HZ / 4;
const RECENT_MAX = 32;
const FROZEN_MIN_FRAMES = 4;
/** Frames whose offsets from the fit `cueTime` takes the median of (one late frame is noise). */
const CUE_FRAMES = 3;
/** Discontinuous frames agree when their offsets are this close. */
const JUMP_AGREE_S = 0.05;
/** Observations before the settling re-anchor. */
const SETTLE_FRAMES = 12;
/** Clean frames before tick-locked one-shots trust the clock. */
const STABLE_FRAMES = 4;

export interface TickClock {
  /** Context time (s) at which round tick `tick` is heard. */
  audioTime(tick: number): number;
  /** Round tick heard at context time `t`. */
  tickAt(t: number): number;
}

/** Beats ↔ round ticks for one round (§5). */
export class TempoMap {
  readonly sdBeat: number;

  constructor(readonly tpb: number, readonly tpbSD: number = tpb, readonly sdStart: number = Infinity) {
    this.sdBeat = sdStart / tpb;
  }

  tickAtBeat(beat: number): number {
    return beat < this.sdBeat ? beat * this.tpb : this.sdStart + (beat - this.sdBeat) * this.tpbSD;
  }

  beatAtTick(tick: number): number {
    return tick < this.sdStart ? tick / this.tpb : this.sdBeat + (tick - this.sdStart) / this.tpbSD;
  }

  /** Seconds per beat at `tick`. */
  beatSec(tick: number): number {
    return (tick < this.sdStart ? this.tpb : this.tpbSD) / TICK_HZ;
  }
}

/** Swing (§13.4): `swing` × the unit is added to every off-unit position; units are 16ths or 8ths. */
export function swingBeat(beat: number, swing: number, unitSteps: 1 | 2): number {
  if (swing <= 0) return beat;
  const u = unitSteps / 4;
  const pair = 2 * u;
  const base = Math.floor(beat / pair) * pair;
  const p = beat - base;
  const q = p < u ? p * (1 + swing) : u * (1 + swing) + (p - u) * (1 - swing);
  return base + q;
}

/**
 * The view-locked clock. The frame source's tick does not always advance at exactly 60/s (at a
 * round start prediction establishes its lead and the shown tick runs slow for a moment; ticks
 * jitter by a fraction), so the precise mapping is a robust, exponentially weighted (τ 0.3 s)
 * linear fit of (round tick → context time heard) over the last ~0.8 s of frames: it follows rate changes without lag, ignores outlier
 * frames, and re-anchors after a jump that 3 agreeing frames confirm. The music plays on a fixed
 * 60 ticks/s anchor (`applied`) that re-anchors at bar lines when it strays more than 20 ms.
 */
export class ViewClock implements TickClock {
  private applied = NaN;
  /** Fit: ctx = a + b·(tick − ref) + c·(tick − ref)². */
  private a = NaN;
  private b = 1 / TICK_HZ;
  private c = 0;
  private ref = 0;
  private readonly ticks = new Float64Array(FIT_MAX);
  private readonly ctxs = new Float64Array(FIT_MAX);
  private n = 0;
  private head = 0;
  private jumpFrames = 0;
  private jumpRaw = 0;
  private lastTick = 0;
  /** Observations since the last anchor: the first bar line after the fit has settled takes its
   * estimate even below the 20 ms threshold (the anchor frame's own noise is gone). */
  private settled = 0;
  /** Frames observed since the last anchor or jump. */
  sinceAnchor = 0;
  /** The newest frame's tick, also when the fit rejected it (a stall or a jump still being confirmed). */
  private seenTick = Number.NaN;
  /** Every frame's (tick, ctx) of the last moments, for the freeze check, and its verdict. */
  private readonly recentTicks = new Float64Array(RECENT_MAX);
  private readonly recentCtxs = new Float64Array(RECENT_MAX);
  private recentN = 0;
  private recentHead = 0;
  private frozen = false;

  /**
   * Ticks per second over the last FROZEN_WINDOW_S of frames, Infinity until that much is seen or
   * when few frames arrived in it (a gap between frames is a jump or a host stall, not a freeze).
   */
  private recentRate(tick: number, ctxTime: number): number {
    let rate = Infinity;
    for (let k = 0; k < this.recentN; k++) {
      const i = (this.recentHead - 1 - k + RECENT_MAX) % RECENT_MAX;
      const dt = ctxTime - this.recentCtxs[i];
      if (dt >= FROZEN_WINDOW_S) {
        if (k >= FROZEN_MIN_FRAMES) rate = (tick - this.recentTicks[i]) / dt;
        break;
      }
    }
    this.recentTicks[this.recentHead] = tick;
    this.recentCtxs[this.recentHead] = ctxTime;
    this.recentHead = (this.recentHead + 1) % RECENT_MAX;
    this.recentN = Math.min(RECENT_MAX, this.recentN + 1);
    return rate;
  }
  /** Count of re-anchors (diagnostics). */
  reanchors = 0;
  jumps = 0;

  get anchored(): boolean {
    return Number.isFinite(this.applied);
  }

  /** Anchored and confirmed by a few clean frames (a round's first snapshot often corrects it). */
  get stable(): boolean {
    return this.anchored && this.sinceAnchor >= STABLE_FRAMES;
  }

  /** (precise offset at the newest tick) − applied, seconds. */
  get drift(): number {
    return this.anchored ? this.offsetAt(this.lastTick) - this.applied : 0;
  }

  /**
   * Whether a cue at `tick` may be committed `horizonS` ahead: the newest frame agrees with the fit
   * (no stall or jump being confirmed) and has itself reached within the horizon of the tick. A
   * fit extrapolated past a stalled round tick (prediction settling its lead, a host stall) would
   * otherwise commit a cue up to half a second before its tick is shown.
   */
  due(tick: number, horizonS: number): boolean {
    if (!this.anchored || this.jumpFrames > 0 || this.frozen || !Number.isFinite(this.seenTick)) return false;
    return tick - this.seenTick <= horizonS * TICK_HZ + DUE_SLACK_TICKS;
  }

  /**
   * Context time to commit a cue at `tick`, a few ticks ahead of the newest frame: the fit, unless
   * the newest frames agree that its offset is stale (a short stall it has not absorbed yet: the
   * median of the last 3 frames' offsets from the fit is > 30 ms, above a frame's tick jitter);
   * then the fit shifted by that offset.
   */
  cueTime(tick: number): number {
    const fit = this.fitAt(tick);
    if (this.recentN < CUE_FRAMES) return fit;
    const res: number[] = [];
    for (let k = 0; k < CUE_FRAMES; k++) {
      const i = (this.recentHead - 1 - k + RECENT_MAX) % RECENT_MAX;
      res.push(this.recentCtxs[i] - this.fitAt(this.recentTicks[i]));
    }
    res.sort((p, q) => p - q);
    const off = res[CUE_FRAMES >> 1];
    return Math.abs(off) > CUE_STALE_S ? fit + off : fit;
  }

  reset(): void {
    this.seenTick = Number.NaN;
    this.recentN = 0;
    this.recentHead = 0;
    this.frozen = false;
    this.applied = NaN;
    this.a = NaN;
    this.n = 0;
    this.head = 0;
    this.jumpFrames = 0;
  }

  /** ctx − tick/60 predicted by the fit at `tick`. */
  private offsetAt(tick: number): number {
    return this.fitAt(tick) - tick / TICK_HZ;
  }

  private fitAt(tick: number): number {
    const x = tick - this.ref;
    return this.a + this.b * x + this.c * x * x;
  }

  /**
   * One frame: the source showed round tick `tick` at the moment the context plays `ctxTime`.
   * Returns true when this frame re-anchored the clock at once (first frame or a jump that held
   * for 3 frames: a seek, a new round, a hard resync).
   */
  observe(tick: number, ctxTime: number): boolean {
    if (!Number.isFinite(tick) || !Number.isFinite(ctxTime)) return false;
    this.seenTick = tick;
    // A shown tick that stops advancing (the render timeline waiting on a late snapshot, a host
    // stall) carries no rate: those frames stay out of the fit, so it does not bend toward them.
    // `due` still sees them and holds cues back until the tick moves again.
    const wasFrozen = this.frozen;
    this.frozen = this.recentRate(tick, ctxTime) < FROZEN_RATE;
    if (!this.anchored) {
      this.anchor(tick, ctxTime);
      return true;
    }
    if (this.frozen) return false;
    if (wasFrozen) {
      // The tick runs again, later than the old timeline by however long it stood still: a new one.
      this.jumps++;
      this.anchor(tick, ctxTime);
      return true;
    }
    const predicted = this.fitAt(tick);
    if (Math.abs(ctxTime - predicted) > JUMP_S) {
      // One late frame is an outlier; three in a row that agree with each other are a new timeline.
      const raw = ctxTime - tick / TICK_HZ;
      if (this.jumpFrames === 0 || Math.abs(raw - this.jumpRaw) > JUMP_AGREE_S) {
        this.jumpFrames = 0;
        this.jumpRaw = raw;
      }
      if (++this.jumpFrames < JUMP_FRAMES) return false;
      this.jumps++;
      this.anchor(tick, ctxTime);
      return true;
    }
    this.jumpFrames = 0;
    this.settled++;
    this.sinceAnchor++;
    this.lastTick = tick;
    this.push(tick, ctxTime);
    this.fit();
    return false;
  }

  private anchor(tick: number, ctxTime: number): void {
    this.n = 0;
    this.head = 0;
    this.push(tick, ctxTime);
    this.ref = tick;
    this.a = ctxTime;
    this.b = 1 / TICK_HZ;
    this.c = 0;
    this.applied = ctxTime - tick / TICK_HZ;
    this.lastTick = tick;
    this.jumpFrames = 0;
    this.settled = 0;
    this.sinceAnchor = 0;
  }

  private push(tick: number, ctxTime: number): void {
    this.ticks[this.head] = tick;
    this.ctxs[this.head] = ctxTime;
    this.head = (this.head + 1) % FIT_MAX;
    this.n = Math.min(FIT_MAX, this.n + 1);
  }

  /** Robust least squares over the window: fit, drop residuals > max(3·MAD, 4 ms), refit. */
  private fit(): void {
    const newest = this.ctxs[(this.head - 1 + FIT_MAX) % FIT_MAX];
    const idx: number[] = [];
    for (let k = 0; k < this.n; k++) {
      const i = (this.head - 1 - k + FIT_MAX) % FIT_MAX;
      if (k >= FIT_MIN && newest - this.ctxs[i] > FIT_WINDOW_S) break;
      idx.push(i);
    }
    this.ref = this.lastTick;
    // Exponentially weighted (τ = 0.3 s) quadratic fit: the slope follows the current rate of the
    // shown tick and the curvature follows a rate that is still changing (a round start).
    const solve = (use: readonly number[]): boolean => {
      let s0 = 0, s1 = 0, s2 = 0, s3 = 0, s4 = 0, t0 = 0, t1 = 0, t2 = 0;
      for (const i of use) {
        const w = Math.exp(-(newest - this.ctxs[i]) / FIT_TAU_S);
        const x = this.ticks[i] - this.ref;
        const y = this.ctxs[i] - this.ref / TICK_HZ;
        const x2 = x * x;
        s0 += w;
        s1 += w * x;
        s2 += w * x2;
        s3 += w * x2 * x;
        s4 += w * x2 * x2;
        t0 += w * y;
        t1 += w * x * y;
        t2 += w * x2 * y;
      }
      let a: number;
      let b = 1 / TICK_HZ;
      let c = 0;
      const lin = s0 * s2 - s1 * s1;
      if (use.length >= 12) {
        // 3×3 normal equations by Cramer's rule.
        const det = s0 * (s2 * s4 - s3 * s3) - s1 * (s1 * s4 - s3 * s2) + s2 * (s1 * s3 - s2 * s2);
        if (Math.abs(det) > 1e-9 * Math.max(1, s0 * s2 * s4)) {
          a = (t0 * (s2 * s4 - s3 * s3) - s1 * (t1 * s4 - s3 * t2) + s2 * (t1 * s3 - s2 * t2)) / det;
          b = (s0 * (t1 * s4 - s3 * t2) - t0 * (s1 * s4 - s3 * s2) + s2 * (s1 * t2 - t1 * s2)) / det;
          c = (s0 * (s2 * t2 - t1 * s3) - s1 * (s1 * t2 - t1 * s2) + t0 * (s1 * s3 - s2 * s2)) / det;
        } else {
          a = t0 / s0;
        }
      } else if (use.length >= 6 && lin > s0 * s0 * 4) {
        b = (s0 * t1 - s1 * t0) / lin;
        a = (t0 - b * s1) / s0;
      } else {
        a = (t0 - b * s1) / s0;
      }
      // Physical bounds: the shown tick runs between 0.5× and 2× real time, and its rate changes
      // by at most 2× per second.
      if (!(b >= 0.5 / TICK_HZ && b <= 2 / TICK_HZ)) {
        b = Math.min(2 / TICK_HZ, Math.max(0.5 / TICK_HZ, Number.isFinite(b) ? b : 1 / TICK_HZ));
        c = 0;
        a = (t0 - b * s1) / s0;
      }
      c = Math.max(-MAX_CURVE, Math.min(MAX_CURVE, c));
      if (!Number.isFinite(a)) return false;
      this.a = a + this.ref / TICK_HZ;
      this.b = b;
      this.c = c;
      return true;
    };
    if (!solve(idx)) return;
    if (idx.length >= 6) {
      const res = idx.map((i) => this.ctxs[i] - this.fitAt(this.ticks[i]));
      const sorted = res.map(Math.abs).sort((x, y) => x - y);
      const mad = sorted[sorted.length >> 1];
      const lim = Math.max(OUTLIER_FLOOR_S, 3 * mad);
      const keep = idx.filter((_, k) => Math.abs(res[k]) <= lim);
      if (keep.length >= 4 && keep.length < idx.length) solve(keep);
    }
  }

  /** The sequencer reaches a bar line: correct drift above 20 ms. Returns true if it re-anchored. */
  barLine(): boolean {
    if (!this.anchored) return false;
    const target = this.offsetAt(this.lastTick);
    const d = Math.abs(target - this.applied);
    if (this.settled >= SETTLE_FRAMES && this.settled < Number.MAX_SAFE_INTEGER) {
      this.settled = Number.MAX_SAFE_INTEGER;
      if (d > 0.0005) {
        this.applied = target;
        return true;
      }
    }
    if (d <= DRIFT_REANCHOR_S) return false;
    this.applied = target;
    this.reanchors++;
    return true;
  }

  /** The music's steady 60 ticks/s mapping. */
  audioTime(tick: number): number {
    return this.applied + tick / TICK_HZ;
  }

  /** The best current estimate (for one-shot tick-locked cues ≤ ~0.3 s ahead). */
  preciseTime(tick: number): number {
    return this.fitAt(tick);
  }

  tickAt(t: number): number {
    return (t - this.applied) * TICK_HZ;
  }
}

/** A free-running clock (menus, lobby, intermission): tick 0 at `origin`. */
export class FreeClock implements TickClock {
  constructor(readonly origin: number) {}

  audioTime(tick: number): number {
    return this.origin + tick / TICK_HZ;
  }

  tickAt(t: number): number {
    return (t - this.origin) * TICK_HZ;
  }
}
